import { useContext, useState } from 'react'
import { Button, InlineAlert, TextArea } from '../BaseComponents'
import Label from '../BaseComponents/Label'
import useApi from '../../hooks/useApi'
import useUserData from '../../hooks/useUserData'
import { userContext } from '../../context/UserContext'
import { getCSRFToken } from '../../lib/utils'

type UpgradeRequestFormProps = {
	// Used for the DOM ids, so the form can appear on a page more than once without collisions.
	idPrefix: string
	kind: 'upgrade' | 'support'
	intro?: string
	reasonLabel: string
	reasonPlaceholder: string
	requireReason?: boolean
	submitText: string
	pendingText: string
}

// A Limited user's request for a real organisation. Hidden when the user cannot request one; a pending
// request shows a notice but never blocks sending another.
function UpgradeRequestForm({ idPrefix, kind, intro, reasonLabel, reasonPlaceholder, requireReason, submitText, pendingText }: UpgradeRequestFormProps) {
	const userData = useUserData()
	const { fetchUserData } = useContext(userContext)
	const api = useApi()
	const [reason, setReason] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	if (!userData.canRequestUpgrade) {
		return null
	}

	const onSubmit = async () => {
		setSubmitting(true)
		setError(null)
		try {
			await api.post('/training-user/request-upgrade', {
				_csrf: await getCSRFToken(),
				reason: reason.trim() || undefined,
				kind,
			})
			await fetchUserData?.()
			setReason('')
		} catch (_) {
			setError('Failed to submit the request. Please try again.')
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<div>
			{intro && <div>{intro}</div>}
			<Label htmlFor={`${idPrefix}-reason`} className={`block ${intro ? 'mt-2' : ''}`}>{reasonLabel}</Label>
			<div className='mt-2 max-w-[32rem]'>
				<TextArea
					id={`${idPrefix}-reason`}
					className='block resize-y'
					placeholder={reasonPlaceholder}
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					rows={3}
					maxLength={1000}
				/>
			</div>
			{error && <InlineAlert type='error' className='mt-3'>{error}</InlineAlert>}
			<div className='mt-3'>
				<Button
					requiresWrite
					type='Primary'
					size='sm'
					text={submitting ? 'Sending...' : submitText}
					onClick={onSubmit}
					disabled={submitting || (!!requireReason && reason.trim() === '')}
				/>
			</div>
			{userData.requestedUpgradeAt && (
				<InlineAlert type='info' className='mt-3' data-testid={`${idPrefix}-pending`}>
					{pendingText}
				</InlineAlert>
			)}
		</div>
	)
}

export default UpgradeRequestForm
